/**
 * Home della piattaforma — landing unica post-login.
 * Ogni sezione ha il suo blocco cinematico completo (immagine, logo colorato,
 * descrizione, bullet e CTA che porta DIRETTAMENTE nella sezione).
 * Le sezioni in lavorazione mostrano la roadmap, senza CTA.
 */
import { ArrowRight, Check, BookOpen, AlertTriangle } from 'lucide-react';
import { useUiStore } from '../../store/ui';
import { usePortal } from '../../store/portal';
import { SECTIONS } from '../../lib/sections';
import { useExtUpdateAvailable } from '../../hooks/useExtTokens';
import { EXT_VERSION } from '../../lib/extension';

export function HomeView() {
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const setManualiScope = useUiStore((s) => s.setManualiScope);
  const openLinkedIn = useUiStore((s) => s.openLinkedIn);
  const extUpdate = useExtUpdateAvailable();
  const brandTitle = usePortal((s) => s.brandTitle);
  const activeSections = SECTIONS.filter((s) => s.active);
  const comingSections = SECTIONS.filter((s) => !s.active);

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-950">
      <div className="mx-auto max-w-4xl px-6 pb-16 pt-12">
        {/* Hero */}
        <div className="text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-zinc-100 sm:text-5xl">
            La tua comunicazione,
            <br />
            <span style={{ color: 'var(--portal-accent, #38BDF8)' }}>in un posto solo.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-zinc-400">
            {brandTitle} unifica email e canali social con l'aiuto dell'intelligenza artificiale.
            Scegli un canale e inizia.
          </p>
        </div>

        {/* Sezioni attive: banner cinematici con CTA diretta */}
        <div className="mt-12 space-y-6">
          {activeSections.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <div
                key={s.key}
                className="wm-cinema group relative overflow-hidden rounded-2xl border"
              >
                {/* Immagine cinematica di sfondo */}
                <img
                  src={s.image}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-105"
                />
                {/* Velo scuro per leggibilità, più denso dal lato del testo */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: reverse
                      ? 'linear-gradient(270deg, rgba(9,9,11,0.95) 0%, rgba(9,9,11,0.75) 45%, rgba(9,9,11,0.25) 100%)'
                      : 'linear-gradient(90deg, rgba(9,9,11,0.95) 0%, rgba(9,9,11,0.75) 45%, rgba(9,9,11,0.25) 100%)',
                  }}
                />
                <div className={`relative flex min-h-[240px] items-center p-8 sm:p-10 ${reverse ? 'justify-end text-right' : ''}`}>
                  <div className="max-w-md">
                    <div className={`flex items-center gap-3 ${reverse ? 'flex-row-reverse' : ''}`}>
                      <span
                        className="flex h-11 w-11 items-center justify-center rounded-xl"
                        style={{ background: `color-mix(in srgb, ${s.color} 18%, rgba(9,9,11,0.6))`, color: s.color }}
                      >
                        <Icon size={24} strokeWidth={2} />
                      </span>
                      <h2 className="text-2xl font-extrabold tracking-tight text-white">{s.label}</h2>
                      {s.badge && (
                        <span
                          className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{
                            color: s.color,
                            borderColor: `color-mix(in srgb, ${s.color} 45%, transparent)`,
                            background: `color-mix(in srgb, ${s.color} 14%, rgba(9,9,11,0.6))`,
                          }}
                        >
                          {s.badge}
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-base font-semibold text-zinc-100">{s.tagline}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-zinc-300">{s.description}</p>
                    <ul className={`mt-4 space-y-1.5 ${reverse ? 'inline-block text-left' : ''}`}>
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-[13px] text-zinc-200">
                          <Check size={14} className="shrink-0" style={{ color: s.color }} />
                          {b}
                        </li>
                      ))}
                    </ul>
                    {s.key === 'linkedin' && extUpdate && (
                      <button
                        onClick={() => openLinkedIn('extension')}
                        className={`mt-4 inline-flex items-center gap-2 rounded-lg border border-violet-400/60 bg-violet-500/20 px-3 py-1.5 text-[12px] font-semibold text-violet-100 transition hover:bg-violet-500/30 ${reverse ? 'flex-row-reverse' : ''}`}
                        title="Vai all'Estensione per scaricare l'aggiornamento"
                      >
                        <AlertTriangle size={13} className="text-violet-300" />
                        Nuova estensione disponibile (v{EXT_VERSION}) — aggiorna
                      </button>
                    )}
                    <div className={`mt-6 flex flex-wrap items-center gap-3 ${reverse ? 'justify-end' : ''}`}>
                      <button
                        onClick={() => setCurrentView(s.target)}
                        className="inline-flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-bold text-zinc-950 shadow-lg transition-all duration-150 hover:scale-[1.03] hover:brightness-110"
                        style={{ background: s.color }}
                      >
                        Entra in {s.label}
                        <ArrowRight size={16} />
                      </button>
                      {/* Link al manuale d'uso interattivo (stessa guida per tutte le sezioni). */}
                      <button
                        onClick={() => {
                          setManualiScope(s.key === 'linkedin' ? 'linkedin' : null);
                          setCurrentView('manuali');
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-semibold text-zinc-200 transition-colors duration-150 hover:bg-white/10"
                      >
                        <BookOpen size={15} />
                        Manuale
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sezioni in arrivo: card cinematiche compatte */}
        <div className="mt-12 text-center text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          Prossimamente sulla piattaforma
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {comingSections.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className="wm-cinema group relative overflow-hidden rounded-xl border"
              >
                <div className="relative h-28 overflow-hidden">
                  <img
                    src={s.image}
                    alt=""
                    className="h-full w-full object-cover opacity-50 saturate-[0.7] transition-all duration-500 group-hover:scale-105 group-hover:opacity-70 group-hover:saturate-100"
                  />
                  <span className="absolute right-2.5 top-2.5 rounded-full bg-zinc-950/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-400 backdrop-blur">
                    In lavorazione
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-lg"
                      style={{ background: `color-mix(in srgb, ${s.color} 15%, transparent)`, color: s.color }}
                    >
                      <Icon size={17} strokeWidth={2} />
                    </span>
                    <span className="text-[15px] font-bold text-zinc-100">{s.label}</span>
                  </div>
                  <p className="mt-2 text-[13px] leading-relaxed text-zinc-400">{s.tagline}</p>
                  {s.roadmap && <p className="mt-1.5 text-[11px] font-medium text-amber-500/80">{s.roadmap}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
